'use client';
import { useRouter } from 'next/router';
import React, { createContext, useContext, useEffect } from 'react';
import { getUserEmailFromCookies, setRedirectUrlToCookies } from './auth';
import { REDIRECT_URL } from './constants';
import { getLoginPath } from './setup';

type AuthContextProviderProps = {
  children: React.ReactNode;
};

type AuthUserType = {
  email: string;
};

type AuthContextType = {
  user: AuthUserType | null;
  setUser: React.Dispatch<React.SetStateAction<AuthUserType | null>>;
  isLoading: boolean;
};

export const AuthContext = createContext<AuthContextType>({
  user: null,
  setUser: () => {},
  isLoading: true,
});

export function AuthProvider({ children }: AuthContextProviderProps) {
  const [user, setUser] = React.useState<AuthUserType | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  useEffect(() => {
    const userEmailFromCookies = getUserEmailFromCookies();
    if (userEmailFromCookies) setUser({ email: userEmailFromCookies });
    setIsLoading(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, isLoading }}>{children}</AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within a UserProvider');
  }
  return context;
}

/**
 * @description This is a higher order component that wraps the component with authentication logic. It checks if the user is logged in and redirects to the login page if not.
 * @note Need to work on the Loading and Error states presentation
 */
export function withAuth<T extends JSX.IntrinsicAttributes>() {
  return (Component: React.ComponentType<T>) => {
    return function WrappedComponent(props: T) {
      const { user, isLoading } = useAuthContext();
      const router = useRouter();

      if (isLoading) {
        return null;
      }

      if (!user) {
        const redirectUrl = window.location.pathname + window.location.search;
        setRedirectUrlToCookies(REDIRECT_URL, redirectUrl);
        router.push(getLoginPath());
        return null;
      }

      return <Component {...props} />;
    };
  };
}
